/**
 * How a tone from `status-tones` is drawn, in one place.
 *
 * `status-tones` deliberately returns tones and no class names, so each status
 * component maps the tone itself. Two of them had done that separately and
 * disagreed about the resting state, which put a solid ink pill and a pale
 * unset one for the same word on one screen.
 *
 * @module components/shared/tone-presentation
 */

import type { Tone } from "@/components/shared/status-tones";
import type { BadgeTone, NavStatusTone } from "@/components/ui";

/**
 * `ink`, not `neutral`: daisyUI's unset badge is the pale one, which reads as
 * incidental metadata or as a tooltip rather than as a state.
 */
export const TONE_BADGE: Record<Tone, BadgeTone> = {
  success: "success",
  warning: "warning",
  error: "error",
  info: "primary",
  neutral: "ink",
};

/** The icon that carries the meaning for anyone who cannot use colour. */
export const TONE_ICON: Record<Tone, string> = {
  success: "check-circle",
  warning: "clock",
  error: "warning-circle",
  info: "pencil-simple",
  neutral: "circle",
};

/**
 * The sidebar nav dot a tone draws as. `SidebarNavItem`'s dot only has four
 * states, one fewer than `Tone`: `info` reads as the plain "unset" dot, and a
 * genuinely resting `neutral` item carries no dot at all rather than a fifth,
 * redundant grey one.
 */
export const TONE_DOT: Record<Tone, NavStatusTone | undefined> = {
  success: "success",
  warning: "warning",
  error: "error",
  info: "neutral",
  neutral: undefined,
};
